/**
 * Kernel smoothing (Nadaraya-Watson estimator) for sorted `[x, y]`
 * point arrays.
 *
 * Pure computation — no DOM, no stores. Operates on the sorted point
 * arrays that `DataIndex.Series` surfaces. Ported verbatim from
 * `tento/tento-lona-js/src/regression/kernel-smooth.ts` (Phase 3 / 3h).
 *
 * Produces:
 *   - Smoothed regression curve (weighted local mean).
 *   - Confidence bands (weighted local stdev × confFactor).
 *   - Trust signal (ESS × spatial coverage) used by renderers as
 *     the single input for opacity / width / clipping.
 */

import type { Option } from "../core/option.ts";
import { elementAt } from "../core/checked-index.ts";

/** Kernel function: maps distance from center → weight (0 at edges). */
export type KernelFn = (dt: number) => number;

/** A single computed regression point. */
export type RegressionPoint = {
  readonly x: number;
  readonly y: number;
  readonly confidence: number;
  readonly density: number;
  /**
   * Unified support signal in [0, 1]. Encodes how much the data
   * supports the regression at this x. Combines effective sample
   * size, spatial coverage, and distance-to-nearest-data. Renderers
   * use this as the single input for opacity/width/clipping.
   *
   *   - trust = 1        → well-supported, render fully
   *   - trust ≈ 0.5      → edge/transition, fade opacity
   *   - trust < 0.05     → not supported, skip entirely
   */
  readonly trust: number;
};

/** Result of kernel smoothing over a set of points. */
export type KernelSmoothResult = {
  readonly curve: readonly RegressionPoint[];
  readonly xMin: number;
  readonly xMax: number;
};

// ── Kernel functions ──────────────────────────────────────────────

/** The kernel functions a regression smooths with (Gaussian, …). */
export namespace Kernel {
  /** Gaussian kernel: smooth bell curve, 99.7% mass within ±halfWidth. */
  export function gaussian(halfWidth: number): KernelFn {
    const sigma = halfWidth / 3;
    return (dt: number) => {
      if (Math.abs(dt) >= halfWidth) return 0;
      return Math.exp(-(dt * dt) / (2 * sigma * sigma));
    };
  }
}

// ── Kernel smoothing ──────────────────────────────────────────────

/**
 * Compute kernel-smoothed regression over sorted `[x, y]` points.
 *
 * @param points — sorted ascending by x
 * @param halfWidth — kernel window radius
 * @param options — optional overrides
 * @returns regression curve points with confidence, density, trust
 */
export function kernelSmooth(
  points: readonly [number, number][],
  halfWidth: number,
  options: Option<{
    /** Confidence factor (default 1.96 ≈ 95% CI). */
    confFactor?: number;
    /**
     * X-coordinates to evaluate at. If omitted, evaluates at data
     * points plus interpolation points for gaps.
     */
    evalAt?: readonly number[];
    /** Maximum number of output points (default 500). */
    maxPoints?: number;
  }>,
): KernelSmoothResult {
  if (points.length === 0) {
    return { curve: [], xMin: 0, xMax: 0 };
  }

  const confFactor = options?.confFactor ?? 1.96;
  const maxPoints = options?.maxPoints ?? 500;
  const kernel = Kernel.gaussian(halfWidth);

  const xMin = elementAt(points, 0, "kernel-smooth: points")[0];
  const xMax = elementAt(points, points.length - 1, "kernel-smooth: points")[0];

  const evalXs = options?.evalAt
    ? options.evalAt
    : generateEvalPoints(points, halfWidth, maxPoints);

  const curve: RegressionPoint[] = [];
  for (const x of evalXs) {
    curve.push(computeAt(x, points, kernel, halfWidth, confFactor));
  }

  return { curve, xMin, xMax };
}

// ── Imputation ────────────────────────────────────────────────────

/**
 * Fill gaps in a sorted point array by linear interpolation.
 *
 * For daily data (x = DSE), this fills missing days between existing
 * points. `step` controls the granularity (1 = every integer x).
 * Does NOT extrapolate beyond the first/last point.
 *
 * @param points — sorted ascending by x
 * @param step — x increment for imputed points (default 1)
 * @returns new array with original + imputed points, sorted
 */
export function impute(
  points: readonly [number, number][],
  step: number = 1,
): [number, number][] {
  if (points.length < 2) return [...points];

  const result: [number, number][] = [];
  const existing = new Set(points.map(([x]) => x));

  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = elementAt(points, i, "kernel-smooth: points");
    const [x1, y1] = elementAt(points, i + 1, "kernel-smooth: points");
    result.push([x0, y0]);

    const gap = x1 - x0;
    if (gap > step) {
      const steps = Math.floor(gap / step);
      for (let j = 1; j < steps; j++) {
        const x = x0 + j * step;
        if (existing.has(x)) continue;
        const t = (x - x0) / (x1 - x0);
        const y = y0 + t * (y1 - y0);
        result.push([x, y]);
      }
    }
  }
  result.push([...elementAt(points, points.length - 1, "kernel-smooth: points")]);

  return result;
}

// ── Internals ─────────────────────────────────────────────────────

function computeAt(
  x: number,
  points: readonly [number, number][],
  kernel: KernelFn,
  halfWidth: number,
  confFactor: number,
): RegressionPoint {
  let weightedSum = 0;
  let weightedSumSq = 0;
  let weightSum = 0;
  let count = 0;

  // Binary search for the start of the kernel window.
  const minX = x - halfWidth;
  const maxX = x + halfWidth;
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (elementAt(points, mid, "kernel-smooth: points")[0] < minX) lo = mid + 1;
    else hi = mid;
  }

  // Iterate points within the kernel window.
  let weightSumOfSquares = 0;
  for (let i = lo; i < points.length; i++) {
    const [px, py] = elementAt(points, i, "kernel-smooth: points");
    if (px > maxX) break;

    const w = kernel(px - x);
    if (w <= 0) continue;

    weightedSum += w * py;
    weightedSumSq += w * py * py;
    weightSum += w;
    weightSumOfSquares += w * w;
    count++;
  }

  // Density: spatial coverage within the kernel window.
  // 1 - (maxGap / windowSize) so gaps produce low density even when
  // imputed points inflate the sample count.
  let density: number;
  if (count <= 1) {
    density = count === 0 ? 0 : 0.3;
  } else {
    let maxGap = elementAt(points, lo, "kernel-smooth: points")[0] - minX;
    for (let i = lo + 1; i < points.length; i++) {
      if (elementAt(points, i, "kernel-smooth: points")[0] > maxX) break;
      maxGap = Math.max(maxGap, elementAt(points, i, "kernel-smooth: points")[0] - elementAt(points, i - 1, "kernel-smooth: points")[0]);
    }
    let lastInWindow = lo;
    for (let i = lo; i < points.length && elementAt(points, i, "kernel-smooth: points")[0] <= maxX; i++) {
      lastInWindow = i;
    }
    maxGap = Math.max(maxGap, maxX - elementAt(points, lastInWindow, "kernel-smooth: points")[0]);
    const windowSize = maxX - minX;
    density = windowSize > 0
      ? Math.max(0, 1 - maxGap / windowSize)
      : 0;
  }

  if (weightSum === 0) {
    return {
      x,
      y: 0,
      confidence: Number.MAX_SAFE_INTEGER,
      density: 0,
      trust: 0,
    };
  }

  const mean = weightedSum / weightSum;
  const variance = weightedSumSq / weightSum - mean * mean;
  const stdev = Math.sqrt(Math.max(0, variance));

  let confidence: number;
  if (count <= 1 || variance === 0) {
    confidence = confFactor * 0.001;
  } else {
    confidence = confFactor * stdev;
    if (count <= 3) confidence *= 1.5;
  }

  // Trust: effective sample size (Kish's ESS) × spatial coverage.
  // ESS = (Σw)² / Σw². Target: ≥5 for stable estimate.
  const ess = weightSumOfSquares > 0
    ? (weightSum * weightSum) / weightSumOfSquares
    : 0;
  const essFactor = Math.min(1, ess / 5);
  const trust = Math.max(0, Math.min(1, essFactor * density));

  return { x, y: mean, confidence, density, trust };
}

/**
 * Generate evaluation x-coordinates: at data points plus
 * interpolation points to fill gaps for smooth curves.
 */
function generateEvalPoints(
  points: readonly [number, number][],
  halfWidth: number,
  maxPoints: number,
): number[] {
  const xs = new Set<number>();

  for (const [x] of points) xs.add(x);

  for (let i = 0; i < points.length - 1; i++) {
    const x0 = elementAt(points, i, "kernel-smooth: points")[0];
    const x1 = elementAt(points, i + 1, "kernel-smooth: points")[0];
    const gap = x1 - x0;

    // Only fill gaps smaller than the kernel width (larger gaps are
    // between separate data regions).
    if (gap > 1 && gap < halfWidth) {
      const n = Math.min(20, Math.floor(gap) - 1);
      const step = gap / (n + 1);
      for (let j = 1; j <= n; j++) {
        xs.add(Math.floor(x0 + step * j));
      }
    }
  }

  // Add edge points for smooth tails.
  const first = elementAt(points, 0, "kernel-smooth: points")[0];
  const last = elementAt(points, points.length - 1, "kernel-smooth: points")[0];
  const tailPoints = 5;
  const tailStep = halfWidth / (tailPoints + 1);
  for (let j = 1; j <= tailPoints; j++) {
    xs.add(Math.floor(first - tailStep * j));
    xs.add(Math.floor(last + tailStep * j));
  }

  const sorted = Array.from(xs).sort((a, b) => a - b);
  if (sorted.length <= maxPoints) return sorted;

  const step = (sorted.length - 1) / (maxPoints - 1);
  const result: number[] = [];
  for (let i = 0; i < maxPoints; i++) {
    result.push(elementAt(sorted, Math.round(i * step), "kernel-smooth: sorted"));
  }
  return result;
}
